import React, { useState, useCallback, useEffect } from 'react';
import { useUrlTab } from '@/hooks/useUrlListState';
import { Button } from '../../components';
import OwnedVehicleTab from './components/OwnedVehicleTab';
import ReturnedVehicleTab from './components/ReturnedVehicleTab';
import { AddVehicleModal } from './components/OwnedVehicle/';
import { vehiclesApi, Vehicle } from '../../services/api/vehicles';
import { getListModel } from '../../services/api/models';
import { getListOem } from '../../services/api/oems';
import {
  showSuccessToast,
  showErrorToast,
} from '../../components/toast/globalToast';
import { extractApiErrorMessage, getUserEmail } from '../../utils';

const VEHICLE_TABS = ['owned', 'returned'] as const;

type Dashboard = {
  totalOwned: number;
  totalReturned: number;
};

const VehiclesPage: React.FC = () => {
  // In the URL with the tab's own filters, so a refresh lands on the same list.
  const [activeTab, setActiveTab] = useUrlTab(VEHICLE_TABS, 'owned');

  const [dashboard, setDashboard] = useState<Dashboard>({
    totalOwned: 0,
    totalReturned: 0,
  });

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [models, setModels] = useState<any[]>([]);
  const [oems, setOEMs] = useState<any[]>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [ownedDownloadParams, setOwnedDownloadParams] = useState<
    Record<string, any>
  >({});
  const [returnedDownloadParams, setReturnedDownloadParams] = useState<
    Record<string, any>
  >({});

  useEffect(() => {
    fetchModels();
    fetchOEMs();
  }, []);

  const fetchModels = async () => {
    try {
      const response = await getListModel({ page: '1', size: '1000' });
      setModels(response.data);
    } catch (err) {
      const errorMessage = extractApiErrorMessage(
        err,
        'Failed to fetch models'
      );
      showErrorToast('Error', errorMessage);
    }
  };

  const fetchOEMs = async () => {
    try {
      const response = await getListOem({ page: '1', size: '1000' });
      setOEMs(response.data);
    } catch (err) {
      const errorMessage = extractApiErrorMessage(err, 'Failed to fetch OEMs');
      showErrorToast('Error', errorMessage);
    }
  };

  const handleModalSubmit = async (vehicleData: any) => {
    try {
      const userEmail = getUserEmail();

      if (editingVehicle) {
        const updateData = { ...vehicleData, updatedBy: userEmail };
        await vehiclesApi.updateVehicle(editingVehicle.ID!, updateData);
        showSuccessToast('Success', 'Vehicle updated successfully');
      } else {
        const createData = { ...vehicleData, createdBy: userEmail };
        await vehiclesApi.createVehicle(createData);
        showSuccessToast('Success', 'Vehicle created successfully');
      }

      setIsAddModalOpen(false);
      setEditingVehicle(null);

      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      const fallbackMessage = `Failed to ${
        editingVehicle ? 'update' : 'create'
      } vehicle`;
      const errorMessage = extractApiErrorMessage(error, fallbackMessage);
      showErrorToast('Error', errorMessage);
    }
  };

  const handleDeleteVehicle = async (vehicle: Vehicle) => {
    try {
      const vehicleId = (vehicle as any).ID || vehicle.id;
      const deletedBy = getUserEmail();

      await vehiclesApi.deleteVehicle(vehicleId, deletedBy);
      showSuccessToast('Success', 'Vehicle deleted successfully');
      setIsAddModalOpen(false);
      setEditingVehicle(null);

      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      const errorMessage = extractApiErrorMessage(
        error,
        'Failed to delete vehicle'
      );
      showErrorToast('Error', errorMessage);
    }
  };

  const handleTotalOwnedChange = useCallback((total: number) => {
    setDashboard((prev) => ({
      ...prev,
      totalOwned: total,
    }));
  }, []);

  const handleTotalReturnedChange = useCallback((total: number) => {
    setDashboard((prev) => ({
      ...prev,
      totalReturned: total,
    }));
  }, []);

  const handleTabChange = (tab: 'owned' | 'returned') => {
    setActiveTab(tab);
  };

  const handleAddVehicle = () => {
    setEditingVehicle(null);
    setIsAddModalOpen(true);
  };

  const handleDownloadCsv = async () => {
    setIsDownloading(true);
    try {
      const isOwned = activeTab === 'owned';
      const params = isOwned ? ownedDownloadParams : returnedDownloadParams;
      const blob = isOwned
        ? await vehiclesApi.downloadOwnedCsv(params)
        : await vehiclesApi.downloadReturnedCsv(params);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const dateStamp = new Date().toISOString().slice(0, 10);
      const filePrefix = isOwned ? 'vehicles-owned' : 'vehicles-returned';

      link.href = url;
      link.download = `${filePrefix}_${dateStamp}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSuccessToast('Success', 'Download started');
    } catch (error) {
      const errorMessage = extractApiErrorMessage(
        error,
        'Failed to download vehicles'
      );
      showErrorToast('Error', errorMessage);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="bg-white p-6 max-w-full mx-auto">
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-[24px] font-semibold text-gray-900 mb-1">
              Vehicles
            </h1>
            <p className="text-[14px] text-gray-600">
              Oversee your fleet of EV bikes.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="white"
              size="small"
              onClick={handleDownloadCsv}
              disabled={isDownloading}
            >
              <i className="ri-download-line text-[12px] mr-1"></i>
              {isDownloading ? 'Downloading...' : 'Download'}
            </Button>
            <Button variant="primary" size="small" onClick={handleAddVehicle}>
              <i className="ri-add-line text-[12px] mr-1"></i>
              Create Vehicle
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => handleTabChange('owned')}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === 'owned'
                  ? 'border-[#5e2aac] text-[#5e2aac]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span>Owned Vehicles</span>
              {dashboard.totalOwned > 0 && (
                <span
                  className={`inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none rounded-full ${
                    activeTab === 'owned'
                      ? 'bg-[#5e2aac] text-white'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {dashboard.totalOwned}
                </span>
              )}
            </button>
            <button
              onClick={() => handleTabChange('returned')}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === 'returned'
                  ? 'border-[#5e2aac] text-[#5e2aac]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span>Returned Vehicles</span>
              {dashboard.totalReturned > 0 && (
                <span
                  className={`inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none rounded-full ${
                    activeTab === 'returned'
                      ? 'bg-[#5e2aac] text-white'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {dashboard.totalReturned}
                </span>
              )}
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'owned' && (
        <OwnedVehicleTab
          onAddVehicle={handleAddVehicle}
          onTotalChange={handleTotalOwnedChange}
          onEditVehicle={(vehicle: Vehicle) => {
            setEditingVehicle(vehicle);
            setIsAddModalOpen(true);
          }}
          onDeleteVehicle={handleDeleteVehicle}
          refreshTrigger={refreshTrigger}
          onDownloadParamsChange={setOwnedDownloadParams}
        />
      )}
      {activeTab === 'returned' && (
        <ReturnedVehicleTab
          onTotalChange={handleTotalReturnedChange}
          onDownloadParamsChange={setReturnedDownloadParams}
        />
      )}

      <AddVehicleModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setEditingVehicle(null);
        }}
        onSubmit={handleModalSubmit}
        vehicle={editingVehicle}
        models={models}
        oems={oems}
      />
    </div>
  );
};

export default VehiclesPage;
